import { getMetadata, fetchConfig, fetchPlaceholders } from '../../scripts/aem.js';
import { loadFragment } from '../content-fragment/content-fragment.js';

import './footer.scss';

const SCHOOLNAME_CONFIG_KEY = 'schoolname'; // this must be all lowercase.
const GLOBALFOOTERLINKS_PLACEHOLDER_KEY = 'globalfooterlinks';
const GLOBALFOOTERLOGO_PLACEHOLDER_KEY = 'globalfooterlogo';
const GLOBALFOOTERLOGOLINK_PLACEHOLDER_KEY = 'globalfooterlogolink';
const GLOBALFOOTER_FB_URL_PLACEHOLDER_KEY = 'globalfooterdefaultfburl';
const GLOBALFOOTER_FB_HTML_PLACEHOLDER_KEY = 'globalfooterdefaultfbhtml';

async function getPlaceholderValue(placeHolderKey) {
  try {
    const response = await Promise.resolve(fetchPlaceholders());

    return response?.[placeHolderKey];
  } catch (error) {
    console.error('Error fetching placeholders:', error);
    return undefined;
  }
}

const FOOTER_SECTIONS = {
  0: 'footer__section--school-info',
  1: 'footer__section--contact-us',
  2: 'footer__section--acknowledgement',
  3: 'footer__section--links',
};

function createFooterContainer() {
  const container = document.createElement('div');
  container.classList.add('footer__container');
  return container;
}

function decorateSection(section, sectionIndex) {
  section.classList.add('footer__section', FOOTER_SECTIONS[sectionIndex] || `footer__section--extra-${sectionIndex}`);
}

function wrapHeadingWithList(heading) {
  const ul = heading?.nextElementSibling;

  if (ul && ul?.tagName?.toLowerCase() === 'ul') {
    const wrapper = document.createElement('div');
    wrapper.classList.add('content-group');

    heading.before(wrapper);
    wrapper.appendChild(heading);
    wrapper.appendChild(ul);
  }
}

function isExternalLink(link, baseDomain) {
  const url = new URL(link, window.location.href);
  return url.hostname !== baseDomain;
}

function decorateContactSection(footer, globalFooterDefaultFbURL, globalFooterDefaultFbHTML) {
  const contactWrapper = footer?.querySelector('.footer__section--contact-us .default-content-wrapper');
  if (!contactWrapper) return;

  // The following puts the contact us details in left hand column
  // and socials on right hand column - to cater for multi campus schools
  const parent = contactWrapper.parentNode;
  const parentContactWrapper = document.createElement('div');
  parentContactWrapper.classList.add('parent-default-content-wrapper');
  parent.replaceChild(parentContactWrapper, contactWrapper);
  parentContactWrapper.appendChild(contactWrapper);

  const headings = contactWrapper.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach(wrapHeadingWithList);

  // Put social media section in its own div
  const socialMediaWrapper = document.createElement('div');
  socialMediaWrapper.classList.add('secondary-content-wrapper');
  parentContactWrapper.appendChild(socialMediaWrapper);

  const socialMediaDiv = contactWrapper?.querySelector('#follow-us');
  if (socialMediaDiv) socialMediaWrapper.appendChild(socialMediaDiv?.parentNode);

  // if no follow us section, add a default one
  if (!socialMediaDiv) {
    const contentGroupDiv = document.createElement('div');
    contentGroupDiv.classList.add('content-group');
    socialMediaWrapper.appendChild(contentGroupDiv);
    const followUsHeading = document.createElement('h6');
    followUsHeading.id = 'follow-us';
    followUsHeading.innerText = 'Follow us';
    const defaultSocialsList = document.createElement('ul');
    const facebookLi = document.createElement('li');
    defaultSocialsList.appendChild(facebookLi);
    const facebookLink = document.createElement('a');
    facebookLi.appendChild(facebookLink);
    facebookLink.href = globalFooterDefaultFbURL;
    facebookLink.title = 'facebook';
    const facebookSpan = document.createElement('span');
    facebookSpan.classList.add('icon', 'icon-facebook');
    facebookSpan.innerHTML = globalFooterDefaultFbHTML;
    facebookLink.appendChild(facebookSpan);
    contentGroupDiv.appendChild(followUsHeading);
    contentGroupDiv.appendChild(defaultSocialsList);
  }

  const links = contactWrapper?.querySelectorAll('a') ?? [];
  const baseDomain = window.location.hostname;
  links.forEach((link) => {
    if (isExternalLink(link.href, baseDomain)) {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    }
  });

  // SWS-1396 trim off query string
  const footerLinks = parentContactWrapper.querySelectorAll('a');
  footerLinks.forEach((link) => {
    if (link.href.includes('instagram')) {
      link.href = link.href.replace('?hl=en', '');
    }
  });
}

/**
 * Prepends a section to the footer which contains the school name
 * @param {Element} footer The footer block element
 * @param {string} schoolName The name of the school to be displayed in the footer
 */
function addFirstSection(footer, schoolName) {
  const footerHeadingDiv = document.createElement('div');
  footerHeadingDiv.className = 'section nsw-section footer__section footer__section--heading';
  const footerHeadingContentWrapper = document.createElement('div');
  footerHeadingContentWrapper.className = 'default-content-wrapper';
  const footerHeading = document.createElement('h3');
  footerHeading.innerText = schoolName;
  footerHeadingContentWrapper.append(footerHeading);
  footerHeadingDiv.append(footerHeadingContentWrapper);
  footer.prepend(footerHeadingDiv);
}

/**
 * Add a last section of the footer which includes a logo and a list of global links
 * @param {Element} footer The footer block element
 * @param {string} globalFooterLinksJson JSON string of global footer links
 */
function addLastSection(footer, globalFooterLinksJson, globalFooterLogo, globalFooterLogoLink) {
  const footerLinksDiv = document.createElement('div');
  footerLinksDiv.className = 'section nsw-section footer__section footer__section--links';
  const footerLinksContentWrapper = document.createElement('div');
  footerLinksContentWrapper.className = 'default-content-wrapper';
  const ulElement = document.createElement('ul');

  // Grab global footer links in the global config
  if (globalFooterLinksJson) {
    try {
      const globalFooterLinks = JSON.parse(globalFooterLinksJson);
      globalFooterLinks.forEach((item) => {
        const liElement = document.createElement('li');
        const link = document.createElement('a');
        link.href = item.uri;
        link.innerText = item.linktext;
        liElement.appendChild(link);
        ulElement.appendChild(liElement);
      });
    } catch (error) {
      console.error('Error parsing global footer links JSON:', error);
    }
  }

  const aElement = document.createElement('a');
  aElement.classList.add('footer__logo-cta');
  aElement.href = globalFooterLogoLink;
  const globalLogoPicture = document.createElement('picture');
  const globalLogoImg = document.createElement('img');
  globalLogoImg.setAttribute('src', globalFooterLogo);
  globalLogoPicture.appendChild(globalLogoImg);
  aElement.appendChild(globalLogoPicture);

  footerLinksContentWrapper.append(ulElement);
  footerLinksContentWrapper.append(aElement);
  footerLinksDiv.append(footerLinksContentWrapper);
  footer.append(footerLinksDiv);
}

/**
 * loads and decorates the footer
 * @param {Element} block The footer block element
 */
export default async function decorate(block) {
  // Check if the footer is already loaded to avoid reloading
  if (typeof window !== 'undefined' && window?.footerLoaded) {
    block.textContent = '';
    const footers = document.querySelectorAll('footer');

    if (footers.length > 1) {
      footers.forEach((footer) => {
        if (!footer?.textContent?.trim()) {
          footer.remove(); // Remove the empty footer
        }
      });
    }
    return;
  }
  window.footerLoaded = true;
  // Load footer fragment
  const footerMeta = getMetadata('footer');
  const footerPath = footerMeta
    ? new URL(footerMeta, window.location).pathname
    : '/footer';
  const fragment = await Promise.resolve(loadFragment(footerPath));

  // Create and populate footer container
  const footer = createFooterContainer();
  let sectionIndex = 0;

  const sectionsArray = [...fragment.children];
  sectionsArray.forEach((section) => {
    // If first section already contains school name or logo - remove the section
    // If last section already contains a list or logo - remove the section
    if (!(sectionIndex === 0
      && (section.querySelector('h3') || section.querySelector('picture')))
      && !(sectionIndex === sectionsArray.length - 1
        && (section.querySelector('ul') || section.querySelector('picture')))) {
      footer.append(section);
    }
    sectionIndex += 1;
  });

  // Add a section at the start which contains the school name from the school config file
  const configResponse = await Promise.resolve(fetchConfig());
  const schoolName = configResponse?.[SCHOOLNAME_CONFIG_KEY] || '';
  addFirstSection(footer, schoolName);

  // Add a section at the end which contains the global footer logo and links.
  if (footer?.lastElementChild) {
    const globalFooterLinksJson = await getPlaceholderValue(GLOBALFOOTERLINKS_PLACEHOLDER_KEY);
    const globalFooterLogo = await getPlaceholderValue(GLOBALFOOTERLOGO_PLACEHOLDER_KEY);
    const globalFooterLogoLink = await getPlaceholderValue(GLOBALFOOTERLOGOLINK_PLACEHOLDER_KEY);
    addLastSection(footer, globalFooterLinksJson, globalFooterLogo, globalFooterLogoLink);
  }
  const globalFooterDefaultFbURL = await getPlaceholderValue(GLOBALFOOTER_FB_URL_PLACEHOLDER_KEY);
  const globalFooterDefaultFbHTML = await getPlaceholderValue(GLOBALFOOTER_FB_HTML_PLACEHOLDER_KEY);

  let footerSectionIndex = 0;
  const footerChildrenArray = [...footer.children];
  footerChildrenArray.forEach((footerSection) => {
    decorateSection(footerSection, footerSectionIndex);
    footerSectionIndex += 1;
  });

  // Decorate contact section
  decorateContactSection(footer, globalFooterDefaultFbURL, globalFooterDefaultFbHTML);

  // Update block content.
  block.textContent = '';
  block.append(footer);
}
